import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, EyeOff, Check, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Reset failed');
      }
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex flex-col items-center justify-center p-8">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 mb-8 hover:opacity-80 transition" data-testid="reset-home-btn-invalid">
          <img src="/infuse-logo.jpg" alt="Infuse.AI" className="h-10 w-10 rounded-lg" />
          <span className="text-xl font-bold"><span className="text-white">Infuse</span><span className="text-[#F26419]">.AI</span><span className="text-neutral-400 text-xs align-top">™</span></span>
        </button>
        <div className="bg-[#242424] rounded-2xl p-8 border border-white/10 max-w-md w-full text-center">
          <Shield className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Invalid Reset Link</h2>
          <p className="text-neutral-500 mb-6">This password reset link is missing or invalid.</p>
          <button
            data-testid="go-to-forgot-btn"
            onClick={() => navigate('/forgot-password')}
            className="px-6 py-3 bg-gradient-to-r from-[#F7B731] to-[#F26419] text-black font-semibold rounded-xl"
          >
            Request New Link
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1a1a] flex flex-col items-center justify-center p-8">
      <button onClick={() => navigate('/')} className="flex items-center gap-2 mb-8 hover:opacity-80 transition" data-testid="reset-home-btn">
        <img src="/infuse-logo.jpg" alt="Infuse.AI" className="h-10 w-10 rounded-lg" />
        <span className="text-xl font-bold"><span className="text-white">Infuse</span><span className="text-[#F26419]">.AI</span><span className="text-neutral-400 text-xs align-top">™</span></span>
      </button>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-[#242424] rounded-2xl p-8 border border-white/10">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-[#F7B731] to-[#F26419] flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-black" />
            </div>
            <h2 className="text-2xl font-bold text-white">
              {success ? 'Password Reset' : 'Set New Password'}
            </h2>
            <p className="text-neutral-500 mt-2">
              {success ? 'Your password has been changed successfully.' : 'Enter a new password for your account.'}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
              {error}
            </div>
          )}

          {success ? (
            <div className="space-y-6">
              <div className="flex items-center justify-center gap-3 p-4 bg-green-500/10 border border-green-500/20 rounded-xl text-green-400">
                <Check className="w-5 h-5" />
                <span>Password updated successfully</span>
              </div>
              <button
                data-testid="reset-back-to-login-btn"
                onClick={() => navigate('/login')}
                className="w-full py-4 bg-gradient-to-r from-[#F7B731] to-[#F26419] text-black font-semibold rounded-xl hover:shadow-[0_0_30px_rgba(242,100,25,0.4)] transition-all flex items-center justify-center gap-2"
              >
                Sign In with New Password
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-neutral-400 mb-2">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-600" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    data-testid="reset-password-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-12 pr-12 py-3 bg-black/50 border border-white/10 rounded-xl text-white placeholder-neutral-600 focus:outline-none focus:border-orange-500/50 transition"
                    placeholder="Min 6 characters"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-600 hover:text-neutral-400 transition"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-400 mb-2">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-600" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    data-testid="reset-confirm-input"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-black/50 border border-white/10 rounded-xl text-white placeholder-neutral-600 focus:outline-none focus:border-orange-500/50 transition"
                    placeholder="Repeat password"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                data-testid="reset-submit-btn"
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-[#F7B731] to-[#F26419] text-black font-semibold rounded-xl hover:shadow-[0_0_30px_rgba(242,100,25,0.4)] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                ) : (
                  'Reset Password'
                )}
              </button>

              <button
                type="button"
                data-testid="reset-back-link"
                onClick={() => navigate('/login')}
                className="w-full text-center text-neutral-500 hover:text-orange-400 transition text-sm flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Sign In
              </button>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
}
